import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { symbolFor } from "../currency";

const isUrl = (s) => /^https?:\/\//i.test((s || "").trim());

/**
 * Parses a duration token into whole minutes, or null if it isn't a duration.
 * Accepts "40m", "0m", "2h", "1h30m", "1.5h", "90 m".
 */
export function parseDuration(tok) {
  const s = (tok || "").trim().toLowerCase();
  if (!s) return null;
  const m = s.match(/^(?:(\d+(?:\.\d+)?)\s*h)?\s*(?:(\d+)\s*m)?$/);
  if (!m || (m[1] === undefined && m[2] === undefined)) return null;
  const hours = m[1] !== undefined ? parseFloat(m[1]) : 0;
  const mins = m[2] !== undefined ? parseInt(m[2], 10) : 0;
  return hours * 60 + mins;
}

/** Parses a money token ("$250", "1,250.50", "0") into a number, or null. */
export function parseMoney(tok) {
  const m = (tok || "").trim().match(/^\$?\s*([\d,]+(?:\.\d+)?)$/);
  if (!m) return null;
  const n = Number(m[1].replace(/,/g, ""));
  return isFinite(n) ? n : null;
}

/**
 * Parses one line of the import format. Fields are joined by " - ":
 *
 *   task name - estimate - basecamp link - [assignee]
 *
 * The estimate is either time ("40m", "1h30m") or money ("$250", "250").
 * The link is optional; the assignee (last field) is optional and matched to an
 * employee by exact name. The name may itself contain " - " — it's everything
 * before the estimate, which is the field right before the link (or, when no
 * link is present, the first time/money token after the name).
 *
 * Returns { name, minutes|price, url, assignee } or null if the line doesn't
 * carry a usable estimate.
 */
export function parseLine(line) {
  const parts = line.split(/\s+-\s+/).map((s) => s.trim());
  if (parts.length < 2) return null;

  // Anchor on the link when present (estimate sits right before it); otherwise
  // anchor on the first time/money token after the name.
  const urlIdx = parts.findIndex(isUrl);
  let estIdx = -1;
  if (urlIdx >= 2) {
    estIdx = urlIdx - 1;
  } else {
    for (let i = 1; i < parts.length; i++) {
      if (!isUrl(parts[i]) && (parseDuration(parts[i]) !== null || parseMoney(parts[i]) !== null)) {
        estIdx = i;
        break;
      }
    }
  }
  if (estIdx < 1) return null;

  const name = parts.slice(0, estIdx).join(" - ").trim();
  if (!name) return null;
  const rest = parts.slice(estIdx + 1).filter(Boolean);
  const url = rest.find(isUrl) || null;
  const assignee = rest.filter((s) => !isUrl(s)).pop() || null;

  const minutes = parseDuration(parts[estIdx]);
  if (minutes !== null) return { name, minutes, url, assignee };
  const price = parseMoney(parts[estIdx]);
  if (price !== null) return { name, price, url, assignee };
  return null;
}

/**
 * Parses a pasted list, one task per line in the form
 *   "task name - estimate - basecamp link - assignee"
 * where the estimate is time (for sheets that sync price to hours) or money.
 * Lines without a usable estimate are skipped; empty lines are ignored.
 */
export function parseImport(text) {
  const tasks = [];
  const skipped = [];
  for (const raw of (text || "").split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    const parsed = parseLine(line);
    if (parsed) tasks.push(parsed);
    else skipped.push(line);
  }
  return { tasks, skipped };
}

/** "40m" / "1h 30m" / "2h" for display. */
export function formatMinutes(min) {
  const h = Math.floor(min / 60);
  const m = Math.round(min - h * 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

/**
 * Writes whole minutes as the duration token parseDuration reads back. Exact
 * minutes round-trip as "1h 30m"; anything finer becomes decimal hours
 * ("0.33h"), since the minutes token has to be an integer.
 */
function formatTimeToken(minutes) {
  if (Number.isInteger(minutes)) return formatMinutes(minutes);
  return `${Number((minutes / 60).toFixed(4))}h`;
}

/**
 * The inverse of the estimate parsing above: turns a task's stored estimate
 * back into a token parseDuration/parseMoney accept, so a list written by
 * formatImportLines can be pasted straight back into this modal.
 *
 * **Time is what gets written, whenever time can be worked out.** In order:
 * the task's own hours; failing that, its price converted through the page's
 * `hourlyRate` (a page with a rate knows what its money is worth in time).
 * `client_estimate_hours` holds whatever unit the source page uses
 * (Sheet.time_unit), so `timeUnit` says how to read it, but the token itself is
 * always time, never the page's unit, because the importing page converts time
 * into its own unit. A price with no rate to divide by is the one case that
 * still writes money, bare and with no currency symbol: parseMoney only accepts
 * "$", so a "€" prefix wouldn't come back. Returns null when there's no usable
 * estimate at all.
 */
export function formatEstimate(item, { timeUnit = "hours", hourlyRate = null } = {}) {
  const hours = item?.client_estimate_hours;
  if (hours !== null && hours !== undefined && isFinite(Number(hours))) {
    const n = Number(hours);
    return formatTimeToken(timeUnit === "minutes" ? n : n * 60);
  }
  const price = item?.client_estimate_price;
  if (price !== null && price !== undefined && isFinite(Number(price))) {
    // The rate is per hour whatever the page's time_unit is, so the division
    // gives hours and the unit never enters into it.
    const rate = Number(hourlyRate);
    if (isFinite(rate) && rate > 0) return formatTimeToken((Number(price) / rate) * 60);
    const n = Number(price);
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
  }
  return null;
}

/**
 * Serializes tasks back into the "task name - estimate - link - assignee"
 * import format, one per line, so a batch can be downloaded and later pasted
 * into this modal on any page. `assignee` (optional) is appended to every line;
 * the admin's import matches it to an employee by exact name. An item can carry
 * its own `assignee` and that wins: a proposal batch goes wholesale to the
 * proposer, but real tasks exported off a page are each assigned separately.
 *
 * A task with no usable estimate is written as its name alone. That line won't
 * parse on the way back in, but it's in the file to fix by hand rather than
 * silently dropped. The assignee is only written next to a real estimate for
 * the same reason: without one the line doesn't parse at all.
 */
export function formatImportLines(
  items,
  { timeUnit = "hours", hourlyRate = null, assignee = null } = {}
) {
  return (items || [])
    .map((item) => {
      const parts = [String(item?.name ?? "").trim()];
      const est = formatEstimate(item, { timeUnit, hourlyRate });
      const who = item?.assignee ?? assignee;
      if (est) parts.push(est);
      if (item?.url) parts.push(item.url);
      if (est && who) parts.push(who);
      return parts.join(" - ");
    })
    .join("\n");
}

export default function ImportTasksModal({
  open,
  onClose,
  onImport,
  currency = "USD",
  employees = [],
  priceSyncEnabled = false,
  // Employees can't create tasks directly — this submits a batch for the admin
  // to review instead. Same parsing, but no "mark agreed" (that's the admin's
  // call) and any assignee typed in a line is dropped: proposed tasks are always
  // assigned to whoever submits them once approved.
  employeeMode = false,
}) {
  const [text, setText] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const symbol = symbolFor(currency);

  // Lookup employee by exact name (trimmed, case-insensitive).
  const empByName = useMemo(() => {
    const map = new Map();
    for (const e of employees || []) {
      if (e?.name) map.set(e.name.trim().toLowerCase(), e);
    }
    return map;
  }, [employees]);

  // Parse, resolve assignee names to employee ids, and (on a price-synced page)
  // reject money estimates — there the price is derived from time, not entered.
  const { tasks, skipped, rejected } = useMemo(() => {
    const { tasks: parsed, skipped } = parseImport(text);
    const resolved = parsed.map((t) => {
      if (!t.assignee) return t;
      const e = empByName.get(t.assignee.trim().toLowerCase());
      return { ...t, assigneeUserId: e ? e.id : null };
    });
    if (!priceSyncEnabled) return { tasks: resolved, skipped, rejected: [] };
    const tasks = [];
    const rejected = [];
    for (const t of resolved) {
      if (t.minutes === undefined || t.minutes === null) rejected.push(t);
      else tasks.push(t);
    }
    return { tasks, skipped, rejected };
  }, [text, empByName, priceSyncEnabled]);

  useEffect(() => {
    if (!open) {
      setText("");
      setAgreed(false);
      setBusy(false);
      setErr(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose, busy]);

  if (!open) return null;

  const handleImport = async () => {
    if (tasks.length === 0) return;
    setBusy(true);
    setErr(null);
    try {
      await onImport(tasks, { agreed });
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 640 }}
        role="dialog"
        aria-modal="true"
      >
        <h3>{employeeMode ? "Propose tasks" : "Import tasks"}</h3>
        <p className="muted" style={{ marginTop: 8 }}>
          Paste a list, one task per line in the form{" "}
          <em>task name - estimate - link - assignee</em>. The estimate is time
          (e.g. <em>40m</em>, <em>1h30m</em>)
          {priceSyncEnabled ? (
            <> — this page derives price from time, so money estimates aren't allowed</>
          ) : (
            <> or money (e.g. <em>$250</em>)</>
          )}
          ; the link and assignee are optional. Time is converted to this page's
          unit automatically.{" "}
          {employeeMode ? (
            <>Any assignee you type is ignored — proposed tasks are always
            assigned to you once approved. These aren't added right away: an
            admin reviews and approves the batch first.</>
          ) : (
            <>The assignee is matched to an employee by exact name.</>
          )}
        </p>

        <div style={{ marginTop: 16 }}>
          <label className="field">List</label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={10}
            spellCheck={false}
            placeholder={
              "Create discount on Invoice - 40m - https://… - Gurur\nInvoice is Unpaid but balance $0 - 15m - https://… - Gurur"
            }
            style={{ fontFamily: "var(--font-mono)", fontSize: "0.85rem" }}
            autoFocus
          />
        </div>

        {(tasks.length > 0 || skipped.length > 0 || rejected.length > 0) && (
          <div style={{ marginTop: 14 }}>
            <div className="muted-sm" style={{ marginBottom: 8 }}>
              {tasks.length} task{tasks.length === 1 ? "" : "s"} parsed
              {rejected.length > 0 && <> · {rejected.length} rejected</>}
              {skipped.length > 0 && <> · {skipped.length} line{skipped.length === 1 ? "" : "s"} skipped</>}
            </div>
            {rejected.length > 0 && (
              <div className="error" style={{ marginBottom: 10 }}>
                {rejected.length} line{rejected.length === 1 ? "" : "s"} rejected: a
                money estimate isn't allowed on a page that derives price from time.
                Use a time estimate (e.g. <em>40m</em>) instead:
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  {rejected.map((t, i) => (
                    <li key={i}>{t.name}</li>
                  ))}
                </ul>
              </div>
            )}
            {tasks.length > 0 && (
              <div className="import-preview">
                {tasks.map((t, i) => (
                  <div key={i} className="import-row">
                    <span className="import-name">
                      {t.url ? (
                        <a href={t.url} target="_blank" rel="noreferrer">{t.name}</a>
                      ) : (
                        t.name
                      )}
                      {!employeeMode && t.assignee && (
                        <span
                          className={`import-assignee${t.assigneeUserId ? "" : " is-unmatched"}`}
                          title={
                            t.assigneeUserId
                              ? `Assign to ${t.assignee}`
                              : `No employee named "${t.assignee}" — will be left unassigned`
                          }
                        >
                          {t.assignee}{t.assigneeUserId ? "" : " ?"}
                        </span>
                      )}
                    </span>
                    <span className="import-price">
                      {t.minutes !== undefined
                        ? formatMinutes(t.minutes)
                        : `${symbol}${t.price}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {err && <div className="error" style={{ marginTop: 14 }}>{err}</div>}

        <div
          className="row-h"
          style={{ justifyContent: employeeMode ? "flex-end" : "space-between", marginTop: 20, gap: 12 }}
        >
          {!employeeMode && (
            <label className="check-row" style={{ margin: 0 }}>
              <input
                type="checkbox"
                className="cbx"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                disabled={busy}
              />
              Mark all as agreed
            </label>
          )}
          <div className="row-h" style={{ gap: 8 }}>
            <button type="button" onClick={onClose} disabled={busy}>Cancel</button>
            <button
              type="button"
              className="primary"
              onClick={handleImport}
              disabled={busy || tasks.length === 0}
            >
              {busy
                ? employeeMode ? "Submitting…" : "Importing…"
                : employeeMode
                ? `Submit ${tasks.length || ""} task${tasks.length === 1 ? "" : "s"} for review`
                : `Import ${tasks.length || ""} task${tasks.length === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
